import React, { useState, useEffect, useContext } from 'react';
import { IoClose } from 'react-icons/io5';
import { AppContext } from '../../context/appContext';
import { getAudioCategories } from '../api/audioCategoryApi';
import { getVideoCategories } from '../api/videoCategoryApi';
import { getBookCategories } from '../api/bookCategoryApi';
import { getCategories as getMiscCategories } from '../api/categoryApi';

/**
 * CategoryPickerModal - Browse and select categories (audio/video/book)
 * @param {string} type - 'audio' | 'video' | 'book' | 'misc' | 'series'
 * @param {boolean} isOpen - Modal open state
 * @param {function} onClose - Close modal callback
 * @param {function} onSelect - Selection callback (categoryId, categoryTitle)
 */
const CategoryPickerModal = ({ type, isOpen, onClose, onSelect }) => {
  const { t, isRTL } = useContext(AppContext);

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    if (isOpen) {
      fetchCategories();
    }
  }, [isOpen, type, search, currentPage]);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      let response;
      switch (type) {
        case 'audio':
          response = await getAudioCategories(currentPage, itemsPerPage, search, false);
          break;
        case 'video':
          // Include series (categoryType=all) so series like برگزیدگان can be deep-link targets.
          response = await getVideoCategories(currentPage, itemsPerPage, search, false, undefined, 'all');
          break;
        case 'series':
          // Only top-level series (parentId=null, categoryType=series) — pick a series to deep-link to.
          response = await getVideoCategories(currentPage, itemsPerPage, search, false, 'null', 'series');
          break;
        case 'book':
          response = await getBookCategories(currentPage, itemsPerPage, search, false);
          break;
        case 'misc':
          // Categories of the More (بیشتر) section — flat list, includes subcategories.
          response = await getMiscCategories(currentPage, itemsPerPage, search, 'false');
          break;
        default:
          response = { data: { data: [] } };
      }

      // Handle response structure: response.data.data is the array
      if (response?.data?.data && Array.isArray(response.data.data)) {
        setCategories(response.data.data);
        if (response.data.pagination) {
          setTotalPages(response.data.pagination.totalPages || 1);
        }
      } else {
        console.warn('Unexpected response structure:', response);
        setCategories([]);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (category) => {
    onSelect({
      categoryId: category._id,
      categoryTitle: category.title,
      parentId: category.parentId
    });
    onClose();
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setCurrentPage(1);
  };

  const getCategoryTypeLabel = () => {
    switch (type) {
      case 'audio':
        return t('common.audioCategory') || 'Audio Category';
      case 'video':
        return t('common.videoCategory') || 'Video Category';
      case 'book':
        return t('common.bookCategory') || 'Book Category';
      case 'series':
        return t('common.series') || 'Series';
      case 'misc':
        return t('common.miscCategory') || 'More (بیشتر) Category';
      default:
        return t('common.category') || 'Category';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal modal-open">
      <div className="modal-box max-w-2xl" dir={isRTL ? 'rtl' : 'ltr'}>
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-bold text-lg">
            {t('common.select') || 'Select'} {getCategoryTypeLabel()}
          </h3>
          <button onClick={onClose} className="btn btn-sm btn-circle btn-ghost">
            <IoClose size={20} />
          </button>
        </div>

        {/* Search */}
        <div className="mb-4">
          <input
            type="text"
            placeholder={t('common.search') || 'Search...'}
            className="input input-bordered w-full"
            value={search}
            onChange={handleSearchChange}
          />
        </div>

        {/* Category List */}
        <div className="overflow-y-auto max-h-96">
          {loading ? (
            <div className="flex justify-center py-8">
              <span className="loading loading-spinner loading-md"></span>
            </div>
          ) : categories.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              {t('common.noResults') || 'No categories found'}
            </div>
          ) : (
            <div className="space-y-2">
              {categories.map(category => (
                <div
                  key={category._id}
                  onClick={() => handleSelect(category)}
                  className="p-3 border rounded-lg hover:bg-base-200 cursor-pointer transition-colors"
                >
                  <div className="font-medium">{category.title}</div>
                  {category.description && (
                    <div className="text-sm text-gray-500 mt-1">
                      {category.description}
                    </div>
                  )}
                  {category.parentId && (
                    <div className="text-xs text-gray-400 mt-1">
                      {t('common.subcategory') || 'Subcategory'}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center gap-2 mt-4">
            <button
              className="btn btn-sm"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              {t('common.previous') || 'Previous'}
            </button>
            <span className="py-2 px-3">
              {currentPage} / {totalPages}
            </span>
            <button
              className="btn btn-sm"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              {t('common.next') || 'Next'}
            </button>
          </div>
        )}

        {/* Cancel Button */}
        <div className="modal-action">
          <button onClick={onClose} className="btn">
            {t('common.cancel') || 'Cancel'}
          </button>
        </div>
      </div>
      <div className="modal-backdrop" onClick={onClose}></div>
    </div>
  );
};

export default CategoryPickerModal;
